/**
 * Shared form fields for bridge session objective: objective text, valuations, revenue, expense, months, optional commitment toll.
 * Used by CreateBridgeSessionWizard and EditBridgeSessionObjectiveModal.
 */
import React, { useMemo } from 'react';
import { Form, Input, Select, InputNumber, Typography, Divider } from 'antd';
import type { FormInstance } from 'antd';

const { TextArea } = Input;
const { Text } = Typography;

const DOMAIN_OPTIONS = [
  { value: 'Business', label: 'Business' },
  { value: 'Strategy', label: 'Strategy' },
  { value: 'Resource', label: 'Resource' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'MarketFit', label: 'MarketFit' },
];
const DAYS_PER_MONTH = 30;

export interface BridgeSessionObjectiveFormValues {
  domain?: string;
  objective?: string;
  current_valuation?: number;
  future_valuation?: number;
  current_revenue?: number;
  current_expense?: number;
  estimated_months?: number;
  cost_one_hour_value?: number | null;
}

export interface BridgeSessionObjectiveFormProps {
  form: FormInstance<BridgeSessionObjectiveFormValues>;
  /** Show domain selector (create flow). */
  showDomain?: boolean;
  /** Show commitment toll section (create flow; not persisted yet). */
  showCommitmentToll?: boolean;
  /** Show value-per-day/hour estimate. */
  showEstimate?: boolean;
}

export const BridgeSessionObjectiveForm: React.FC<BridgeSessionObjectiveFormProps> = ({
  form,
  showDomain = false,
  showCommitmentToll = false,
  showEstimate = true,
}) => {
  const currentV = Form.useWatch('current_valuation', form) ?? 0;
  const futureV = Form.useWatch('future_valuation', form) ?? 0;
  const months = Form.useWatch('estimated_months', form) ?? 0;

  const valuePerDay = useMemo(() => {
    if (!showEstimate || !months || months <= 0) return null;
    const totalDays = months * DAYS_PER_MONTH;
    const delta = (futureV || 0) - (currentV || 0);
    return delta / totalDays;
  }, [showEstimate, months, futureV, currentV]);

  const valuePerHour = useMemo(() => {
    if (valuePerDay == null) return null;
    return valuePerDay / 8;
  }, [valuePerDay]);

  return (
    <>
      {showDomain && (
        <>
          <Form.Item name="domain" label="Session domain" rules={[{ required: true }]}>
            <Select options={DOMAIN_OPTIONS} placeholder="Select domain" />
          </Form.Item>
        </>
      )}
      <Form.Item
        name="objective"
        label="Next milestone / objective for the domain"
        rules={[{ required: true, message: 'Enter the bridge objective' }]}
      >
        <TextArea rows={2} placeholder="e.g. Reach $2M ARR with positive unit economics" />
      </Form.Item>

      <Divider style={{ margin: '16px 0' }} />

      <Form.Item
        name="estimated_months"
        label="Estimated months to transition"
        rules={[{ required: true }]}
        style={{ marginBottom: 16 }}
      >
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <InputNumber min={1} style={{ width: 160 }} placeholder="e.g. 12" />
        </div>
      </Form.Item>

      <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
        <Form.Item
          name="current_valuation"
          label="Current state — Current valuation"
          rules={[{ required: true }]}
          style={{ flex: 1 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 500000" />
        </Form.Item>
        <Form.Item
          name="future_valuation"
          label="Future state — Future valuation"
          rules={[{ required: true }]}
          style={{ flex: 1 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 2000000" />
        </Form.Item>
      </div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
        <Form.Item
          name="current_revenue"
          label="Current state — Current revenue"
          rules={[{ required: true, message: 'Enter current revenue' }]}
          style={{ flex: 1 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 400000" />
        </Form.Item>
        <Form.Item
          name="current_expense"
          label="Current state — Current expense"
          rules={[{ required: true, message: 'Enter current expense' }]}
          style={{ flex: 1 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g. 350000" />
        </Form.Item>
      </div>

      {showEstimate && (valuePerDay != null || valuePerHour != null) && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
          <Text strong>Estimate</Text>
          <div style={{ marginTop: 8 }}>
            {valuePerDay != null && (
              <div>
                <Text type="secondary">Value per day: </Text>
                <Text>
                  {valuePerDay.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
            )}
            {valuePerHour != null && (
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">Value per hour: </Text>
                <Text>
                  {valuePerHour.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {showCommitmentToll && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <div style={{ marginBottom: 16 }}>
            <Text strong>Commitment toll</Text>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <Text type="secondary">time — 1 hour session</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary">cost — 1 hour value</Text>
                <Form.Item name="cost_one_hour_value" noStyle>
                  <InputNumber min={0} placeholder="Enter amount" style={{ width: 140 }} />
                </Form.Item>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default BridgeSessionObjectiveForm;
